import { useCallback, useEffect, useState } from "react";

import isNil from "lodash/isNil";
import { useTranslation } from "react-i18next";

import { MetricRangeType } from "@waterly/graphql-types";
import type { MetricRangeInput } from "@waterly/utils-isomorphic";
import { MetricRangeValidationErrorCode, validateMetricRanges } from "@waterly/utils-isomorphic";

import { calculateInclusivity, calculateRangeMinMax } from "./calculateInclusivity";
import type { LimitRangeSide, MetricRangeCardMetricRange } from "./metricRangeUtils";
import { buildRangeExpression, isSingletonRange, sortMetricRangesByBounds } from "./metricRangeUtils";

export { calculateInclusivity } from "./calculateInclusivity";

type ValidateBeforeSaveReturn = { success: boolean; translatedError?: string };

export type MetricRangeFieldErrors = {
  nameError?: string;
  limitError?: string;
};

export type IdealMetricRangeErrors = Record<LimitRangeSide, string | undefined> & {
  nameError?: string;
};

const hasMetricRangeFieldErrors = (errors: MetricRangeFieldErrors | undefined) =>
  [errors?.nameError, errors?.limitError].some(Boolean);

const deriveLimitRangeSide = ({
  range,
  idealRange,
}: {
  range: MetricRangeCardMetricRange;
  idealRange: MetricRangeCardMetricRange | undefined;
}): LimitRangeSide | undefined => {
  if (!idealRange || range.ideal) return undefined;

  if (!isNil(idealRange.rangeMin) && !isNil(range.rangeMax) && range.rangeMax <= idealRange.rangeMin) {
    return MetricRangeType.Lower;
  }

  if (!isNil(idealRange.rangeMax) && !isNil(range.rangeMin) && range.rangeMin >= idealRange.rangeMax) {
    return MetricRangeType.Upper;
  }

  return undefined;
};

export const useMetricRangeErrorsManager = (existingRanges: MetricRangeCardMetricRange[]) => {
  const { t } = useTranslation(["errors"]);
  const [metricRangeErrors, setMetricRangeErrors] = useState<
    Record<string, MetricRangeFieldErrors | undefined>
  >({});
  const [idealMetricRangeErrors, setIdealMetricRangeErrors] = useState<IdealMetricRangeErrors>({
    [MetricRangeType.Lower]: undefined,
    [MetricRangeType.Upper]: undefined,
  });

  const getRangeAndAdjacentRangesById = useCallback(
    (rangeId: string, ranges: MetricRangeCardMetricRange[] = existingRanges) => {
      const activeSortedRanges = ranges
        .filter((range) => isNil(range.deletedAt))
        .sort(sortMetricRangesByBounds);
      const rangeIndex = activeSortedRanges.findIndex((range) => range.id === rangeId);
      return {
        previousRangeBorderingRange: activeSortedRanges[rangeIndex - 2],
        previousRange: activeSortedRanges[rangeIndex - 1],
        range: activeSortedRanges[rangeIndex],
        nextRange: activeSortedRanges[rangeIndex + 1],
        nextRangeBorderingRange: activeSortedRanges[rangeIndex + 2],
      } as const;
    },
    [existingRanges],
  );

  const calculateMinMaxByMetricRangeId = useCallback(
    ({
      rangeId,
      side,
      ranges,
    }: {
      rangeId: string;
      side: LimitRangeSide;
      ranges?: MetricRangeCardMetricRange[];
    }) => {
      const { previousRangeBorderingRange, previousRange, range, nextRange, nextRangeBorderingRange } =
        getRangeAndAdjacentRangesById(rangeId, ranges);
      const isPreviousRangeSingleton = isSingletonRange(previousRange);
      const isNextRangeSingleton = isSingletonRange(nextRange);
      const isPreviousRangeBorderingRangeSingleton = isSingletonRange(previousRangeBorderingRange);
      const isNextRangeBorderingRangeSingleton = isSingletonRange(nextRangeBorderingRange);
      const isPreviousRangeIdeal = Boolean(previousRange?.ideal);
      const isNextRangeIdeal = Boolean(nextRange?.ideal);
      const isPreviousRangeBorderingRangeIdeal = Boolean(previousRangeBorderingRange?.ideal);
      const isNextRangeBorderingRangeIdeal = Boolean(nextRangeBorderingRange?.ideal);

      const rangeExpression = buildRangeExpression({
        ...calculateRangeMinMax({
          previousRange,
          range,
          nextRange,
          side,
          isPreviousRangeSingleton,
          isNextRangeSingleton,
          isPreviousRangeIdeal,
          isNextRangeIdeal,
        }),
        ...calculateInclusivity({
          ideal: range.ideal,
          side,
          isPreviousRangeSingleton,
          isNextRangeSingleton,
          isPreviousRangeBorderingRangeSingleton,
          isNextRangeBorderingRangeSingleton,
          isPreviousRangeIdeal,
          isNextRangeIdeal,
          isPreviousRangeBorderingRangeIdeal,
          isNextRangeBorderingRangeIdeal,
        }),
      });

      return t("common:value_must_be_in_range", { range: rangeExpression });
    },
    [getRangeAndAdjacentRangesById, t],
  );

  const validateBeforeSave = useCallback(
    ({
      proposedRanges,
      editedRangeId,
      side,
    }: {
      proposedRanges: MetricRangeCardMetricRange[];
      editedRangeId: string;
      side: LimitRangeSide;
    }): ValidateBeforeSaveReturn => {
      const activeRanges = proposedRanges.filter((range) => isNil(range.deletedAt));
      const { error } = validateMetricRanges(activeRanges as MetricRangeInput[]);
      const isRangeIdeal = activeRanges.find((range) => range.id === editedRangeId)?.ideal;
      const validationSortedRanges = error?.details?.sortedRanges as MetricRangeCardMetricRange[] | undefined;
      const editedRangeExists = existingRanges.some(
        (range) => range.id === editedRangeId && isNil(range.deletedAt),
      );
      const referenceRanges = editedRangeExists ? undefined : validationSortedRanges ?? activeRanges;

      const value: ValidateBeforeSaveReturn = { success: true };

      //clean uneeded errors
      if (!error) {
        if (isRangeIdeal) {
          setIdealMetricRangeErrors({
            [MetricRangeType.Lower]: undefined,
            [MetricRangeType.Upper]: undefined,
            nameError: undefined,
          });
        } else {
          setMetricRangeErrors((prev) => ({ ...prev, [editedRangeId]: undefined }));
        }
        return value;
      }

      value.success = false;
      switch (error.code) {
        case MetricRangeValidationErrorCode.METRIC_RANGE_NAME_REQUIRED:
          value.translatedError = t(`errors:${error.code}`);
          break;
        case MetricRangeValidationErrorCode.METRIC_RANGE_TAG_MISMATCH:
        case MetricRangeValidationErrorCode.METRIC_RANGE_SINGLETON_MULTIPLE_TAGS:
        case MetricRangeValidationErrorCode.METRIC_RANGE_MULTIPLE_IDEAL:
        case MetricRangeValidationErrorCode.METRIC_RANGE_IDEAL_REQUIRED:
        case MetricRangeValidationErrorCode.METRIC_RANGE_IDEAL_NOT_INCLUSIVE:
          // Terrible if you get into these states naturally
          value.translatedError = "please contact support" + error.code;
          break;
        case MetricRangeValidationErrorCode.METRIC_RANGE_GAP:
        case MetricRangeValidationErrorCode.METRIC_RANGE_OVERLAP:
        case MetricRangeValidationErrorCode.METRIC_RANGE_BOTH_INCLUSIVE:
        case MetricRangeValidationErrorCode.METRIC_RANGE_NEITHER_INCLUSIVE:
        case MetricRangeValidationErrorCode.METRIC_RANGE_MULTIPLE_SINGLETON:
        case MetricRangeValidationErrorCode.METRIC_RANGE_INVALID_SINGLETON:
        case MetricRangeValidationErrorCode.METRIC_RANGE_INVERTED:
          value.translatedError = calculateMinMaxByMetricRangeId({
            rangeId: editedRangeId,
            side,
            ranges: referenceRanges,
          });
          break;
        default:
          // value.translatedError = error.code;
          // @ts-expect-error this error code is in the error.json file, but typescript doesn't know about it'
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          value.translatedError = t(`errors:${error.code}`);
      }

      const isNameError = error.code === MetricRangeValidationErrorCode.METRIC_RANGE_NAME_REQUIRED;

      if (isRangeIdeal && isNameError) {
        setIdealMetricRangeErrors({
          [MetricRangeType.Lower]: undefined,
          [MetricRangeType.Upper]: undefined,
          nameError: value.translatedError,
        });
      } else if (isRangeIdeal) {
        setIdealMetricRangeErrors((prev) => ({
          ...prev,
          [side]: value.translatedError,
          nameError: undefined,
        }));
      } else if (isNameError) {
        setMetricRangeErrors((prev) => ({
          ...prev,
          [editedRangeId]: { nameError: value.translatedError, limitError: undefined },
        }));
      } else {
        setMetricRangeErrors((prev) => ({
          ...prev,
          [editedRangeId]: { nameError: undefined, limitError: value.translatedError },
        }));
      }
      return value;
    },
    [calculateMinMaxByMetricRangeId, existingRanges, t],
  );

  // Keeps range-expression error text synced when neighboring ranges change outside the draft input.
  useEffect(() => {
    const activeRanges = existingRanges.filter((range) => isNil(range.deletedAt));
    const idealRange = activeRanges.find((range) => range.ideal);

    setMetricRangeErrors((previousErrors) => {
      let didChange = false;
      const nextErrors = Object.entries(previousErrors).reduce<
        Record<string, MetricRangeFieldErrors | undefined>
      >((updatedErrors, [erroredRangeId, errors]) => {
        if (!hasMetricRangeFieldErrors(errors)) return updatedErrors;

        const range = activeRanges.find((activeRange) => activeRange.id === erroredRangeId);
        if (!range) {
          didChange = true;
          return updatedErrors;
        }

        const side = deriveLimitRangeSide({ range, idealRange });
        const limitError =
          errors?.limitError && side
            ? calculateMinMaxByMetricRangeId({ rangeId: erroredRangeId, side })
            : errors?.limitError;
        const nextError = { ...errors, limitError };

        if (nextError.nameError !== errors?.nameError || nextError.limitError !== errors?.limitError) {
          didChange = true;
        }

        updatedErrors[erroredRangeId] = nextError;
        return updatedErrors;
      }, {});

      return didChange ? nextErrors : previousErrors;
    });

    setIdealMetricRangeErrors((previousErrors) => {
      if (!idealRange) return previousErrors;

      const nextErrors = {
        ...previousErrors,
        [MetricRangeType.Lower]: previousErrors[MetricRangeType.Lower]
          ? calculateMinMaxByMetricRangeId({ rangeId: idealRange.id, side: MetricRangeType.Lower })
          : previousErrors[MetricRangeType.Lower],
        [MetricRangeType.Upper]: previousErrors[MetricRangeType.Upper]
          ? calculateMinMaxByMetricRangeId({ rangeId: idealRange.id, side: MetricRangeType.Upper })
          : previousErrors[MetricRangeType.Upper],
      };

      return nextErrors[MetricRangeType.Lower] !== previousErrors[MetricRangeType.Lower] ||
        nextErrors[MetricRangeType.Upper] !== previousErrors[MetricRangeType.Upper]
        ? nextErrors
        : previousErrors;
    });
  }, [calculateMinMaxByMetricRangeId, existingRanges]);

  return { validateBeforeSave, metricRangeErrors, idealMetricRangeErrors } as const;
};
